<script lang="ts">
	import { Queue } from '$utils/queue'
	import { isBehindOtherElement } from '$utils/utils.svelte'
	import type { AnalyticalValue } from './types/analyticalValue'
	import { mount, onDestroy, onMount, unmount } from 'svelte'
	import { get, type Writable, writable } from 'svelte/store'
	import tippy, { type Instance } from 'tippy.js'
	import AnalysisErrorTourTooltip from './AnalysisErrorTourTooltip.svelte'
	import { addNotification, dismissNotification } from '../stores'
	import { NotificationType } from '$types/notification'
	import { t } from '$i18n/i18n'

	interface Props {
		errorValues: Writable<AnalyticalValue[]>
		onValueFixed: (value: AnalyticalValue) => void
	}

	let { errorValues, onValueFixed }: Props = $props()

	let queue: Queue<AnalyticalValue> = $state(new Queue($errorValues))

	let currentValue: Writable<AnalyticalValue | undefined> = writable(undefined)
	let notification: symbol | undefined
	let activeTooltip: Instance | null = null
	let componentReady: boolean = false

	const unsubErrorValues = errorValues.subscribe(onErrorValuesChange)
	const unsubCurrentValue = currentValue.subscribe((val) => {
		if (val) {
			const el = document.getElementById(`input-${val.key}`)
			if (el) {
				el.scrollIntoView({ behavior: activeTooltip ? 'smooth' : 'instant', block: 'center' })
				el.focus()
				showTooltip(el)
			}
		}
	})

	function onErrorValuesChange(newValues: AnalyticalValue[]) {
		if (!componentReady) return
		abortTour(false)
		queue = new Queue(newValues)
		if (newValues.length === 0) {
			// No errors in the current view — abort the tour but keep the notification alive.
			// Dismissing here causes duplicate notifications because the derived store that
			// feeds errorValues oscillates during the initial load (transient empty states).
			// The notification is dismissed only when the user finishes all errors (triggerNext)
			// or explicitly cancels the tour (abortTour(true)), NOT, when there were errors, e.g., only
			// for optional values.
			return
		}
		if (!notification) {
			notification = addNotification({
				message: $t('Analyticals.tour.notification'),
				type: NotificationType.ERROR,
				timeout: 0
			})
		}
		initializeTour()
	}

	function showTooltip(target: HTMLElement) {
		destroyTooltip()

		const host = document.createElement('div')

		const component = mount(AnalysisErrorTourTooltip, {
			target: host,
			props: {
				step: totalErrorValues - queue.size + 1,
				totalSteps: totalErrorValues,
				onnext: () => {
					triggerNext()
				},
				onabort: () => {
					abortTour()
				}
			}
		})

		activeTooltip = tippy(target, {
			animation: 'scale-extreme',
			allowHTML: true,
			content: host,
			delay: [100, 200],
			hideOnClick: false,
			interactive: true,
			maxWidth: 375,
			offset: [-1, 13],
			placement: 'top',
			theme: 'mmttip',
			trigger: 'manual',
			showOnCreate: true,
			appendTo: document.querySelector('.main-content')!,
			zIndex: 3, // Needs to be higher than the sidebar, which is 2
			popperOptions: {
				modifiers: [
					{
						name: 'flip',
						options: {
							fallbackPlacements: ['bottom', 'left']
						}
					}
				]
			},
			onDestroy() {
				unmount(component)
			}
		})
	}

	/**
	 * In case opening/closing the sidebar e.g. changes the elements position the tooltip is attached to, we need to
	 * update the tooltip placement after layout changes completed.
	 */
	function updateActiveTooltipPlacement() {
		const instance = activeTooltip
		if (!instance) return

		// Wait for any pending layout changes to apply before measuring/repositioning
		requestAnimationFrame(() => {
			instance.popperInstance?.update()
		})
	}

	function destroyTooltip() {
		activeTooltip?.destroy()
		activeTooltip = null
	}

	function triggerNext() {
		const value = $currentValue

		if (value) {
			onValueFixed(value)
			unmarkField(value.key)
			reenableNormalTooltip(value.key)
			queue.dequeueWhere((v) => v.key === value.key)
		}

		if (queue.isEmpty) {
			dismissNotification(notification)
			destroyTooltip()
			return
		}

		$currentValue = queue.peek()
	}

	function abortTour(removeNotification = true) {
		destroyTooltip()
		queue.toArray().forEach((val) => {
			unmarkField(val.key)
			reenableNormalTooltip(val.key)
		})
		queue.clear()
		$currentValue = undefined
		if (removeNotification) {
			dismissNotification(notification)
			notification = undefined
		}
	}

	function markField(key: string) {
		document.getElementById('input-' + key)?.classList.add('input-field-error')
	}

	function unmarkField(key: string) {
		document.getElementById('input-' + key)?.classList.remove('input-field-error')
	}

	function reenableNormalTooltip(key: string) {
		const tippyToEnable = document.getElementById(`cell-${key}`) as HTMLElement & {
			_tippy?: Instance
		}
		tippyToEnable?._tippy?.enable()
	}

	const handleFocusSwitch = (e: CustomEvent<{ key: string }>) => {
		destroyTooltip()

		let focusedValue = queue.peekWhere((v) => v.key === e.detail.key)
		if (focusedValue) {
			$currentValue = focusedValue
		}
	}

	const handleScroll = () => {
		if ($currentValue && activeTooltip) {
			const el = document.getElementById(`input-${$currentValue.key}`)
			if (el) {
				if (isBehindOtherElement(el)) {
					activeTooltip.hide()
				} else {
					activeTooltip.show()
				}
			}
		}
	}

	function initializeTour(delay: number = 0) {
		return setTimeout(() => {
			// Mark every error value field and disable other tooltip (the cell tooltip) so it is not triggered by the focus event
			queue.toArray().forEach((val) => {
				markField(val.key)
				const tippyToDisable = document.getElementById(`cell-${val.key}`) as HTMLElement & {
					_tippy?: Instance
				}
				tippyToDisable?._tippy?.disable()
			})

			// Start tour if there is no currentValue
			if (!$currentValue) {
				triggerNext()
			}
		}, delay)
	}

	let totalErrorValues = $derived(queue.size)

	// Entry point tour
	onMount(() => {
		componentReady = true

		// @ts-expect-error custom event
		window.addEventListener('focus-input', handleFocusSwitch)
		// If this causes performance problems, try using "scrollend" here, which will not fire that many events.
		window.addEventListener('scroll', handleScroll)
		window.addEventListener('sidebarStateChanged', updateActiveTooltipPlacement)

		let timeout: ReturnType<typeof setTimeout> | undefined

		// errorValues may have been populated by the derived store before onMount fired (before
		// componentReady was true), so we must read the current store value rather than relying
		// on the queue that was initialized at component-creation time when errorValues was [].
		const currentErrors = get(errorValues)
		if (currentErrors.length > 0) {
			queue = new Queue(currentErrors)

			// Show Notification
			if (!notification) {
				notification = addNotification({
					message: $t('Analyticals.tour.notification'),
					type: NotificationType.ERROR,
					timeout: 0
				})
			}

			// Artificial delay (similar to loading animation) to wait for table to be ready before tour start.
			timeout = initializeTour(2300)
		}

		// Cleanup event listeners on destroy
		return () => {
			if (timeout !== undefined) clearTimeout(timeout)
			// @ts-expect-error custom event
			window.removeEventListener('focus-input', handleFocusSwitch)
			window.removeEventListener('scroll', handleScroll)
			window.removeEventListener('sidebarStateChanged', updateActiveTooltipPlacement)
			destroyTooltip()
		}
	})

	onDestroy(() => {
		unsubCurrentValue()
		unsubErrorValues()
	})
</script>
